/**
 * Context: TransactionContext
 * Maneja transacciones globalmente
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import mockDataService from '../services/mockDataService';
import encryptionService from '../services/encryptionService';
import { generateTransactionId } from '../utils/helpers';
import { TRANSACTION_TYPES } from '../utils/constants';

const TransactionContext = createContext(null);

export const TransactionProvider = ({ children }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Carga transacciones al iniciar
  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = useCallback(() => {
    setLoading(true);
    try {
      const txs = mockDataService.getTransactions();
      setTransactions(txs);
    } catch (error) {
      console.error('Error cargando transacciones:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const createTransaction = useCallback((transactionData) => {
    try {
      const transaction = {
        id: generateTransactionId(),
        timestamp: new Date().toISOString(),
        ...transactionData
      };

      // Encripta la transacción
      const encryptedTx = encryptionService.encryptTransaction(transaction);

      // Guarda en el servicio
      mockDataService.createTransaction(encryptedTx);

      // Actualiza el estado
      setTransactions(prev => [encryptedTx, ...prev]);

      return { success: true, transaction: encryptedTx };
    } catch (error) {
      console.error('Error creando transacción:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const getTransactionsByVendor = useCallback((vendorId) => {
    return transactions.filter(t => t.vendedorId === vendorId);
  }, [transactions]);

  const getTransactionsByStudent = useCallback((studentId) => {
    return transactions.filter(t => t.estudianteId === studentId);
  }, [transactions]);

  const getRecentTransactions = useCallback((limit = 10) => {
    return transactions.slice(0, limit);
  }, [transactions]);

  return (
    <TransactionContext.Provider
      value={{
        transactions,
        loading,
        createTransaction,
        getTransactionsByVendor,
        getTransactionsByStudent,
        getRecentTransactions,
        reloadTransactions: loadTransactions
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
};

export const useTransactionContext = () => {
  const context = useContext(TransactionContext);
  
  if (!context) {
    throw new Error('useTransactionContext debe usarse dentro de TransactionProvider');
  }
  
  return context;
};

export default TransactionContext;
